import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { loadModules } from 'esri-loader';
import { useAuth } from '../../../../AuthProvider';
import { addUnavailableNotice, applyDiscernShading, removeRetiredLayers } from '../../../../utils/discernWebmapShading';
import BlockNameHeading from '../../../CustomVestmapViewer/components/BlockNameHeading';
import GenericBlock from '../../../CustomVestmapViewer/components/GenericBlock';
import colors from '../../../VestmapViewer/Demographics/colors.json';
import Groups from '../../../VestmapViewer/Demographics/groups.json';
import marker from '../../../CustomVestmapViewer/assets/marker center.png';

function DemographicGroupBlock({ group }) {
  if (!group) return null;

  const style = {
    colorBox: {
      height: '1rem',
      width: '1rem',
    },
    groupTitle: {
      fontFamily: 'Lato',
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: '20px',
      textAlign: 'left',
      textDecoration: 'underline',
    },
  };

  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: '#F9F9F9',
        paddingY: '1rem',
        paddingX: '1.75rem',
        borderRadius: '1rem',
        display: 'flex',
        gap: '0.75rem',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
      }}
    >
      <BlockNameHeading title='Demographic Group' />
      <Box
        sx={{
          display: 'flex',
          gap: '2rem',
        }}
      >
        <Box
          sx={[
            style.colorBox,
            {
              backgroundColor: `#${colors[group] || '000000'}`,
            },
          ]}
        />
        <a target='_blank' href={Groups[group]} rel='noreferrer'>
          <Typography sx={style.groupTitle}>{group}</Typography>
        </a>
      </Box>
    </Box>
  );
}

function NearestGroupsBlock({ groups }) {
  if (!groups || !Array.isArray(groups) || groups.length === 0) return null;

  const style = {
    colorBox: {
      height: '1rem',
      width: '1rem',
    },
    groupBox: {
      display: 'flex',
      gap: '1.25rem',
      alignItems: 'center',
    },
    groupTitle: {
      fontFamily: 'Lato',
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: '20px',
      textAlign: 'left',
      textDecoration: 'underline',
    },
  };

  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: '#F9F9F9',
        paddingY: '1.25rem',
        paddingX: '3.5rem',
        borderRadius: '1rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          paddingX: '1rem',
          paddingY: '1.5rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '2rem',
        }}
      >
        <BlockNameHeading title='Nearest Groups' />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            maxHeight: '10rem',
            overflowY: 'auto',
          }}
        >
          {groups.map((group, index) => (
            <Box key={index} sx={style.groupBox}>
              <Box
                sx={[
                  style.colorBox,
                  {
                    backgroundColor: `#${colors[group] || '000000'}`,
                  },
                ]}
              />
              <a target='_blank' href={Groups[group]} rel='noreferrer'>
                <Typography sx={style.groupTitle}>{group}</Typography>
              </a>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function WhoWeAreBlock({ blockValue }) {
  if (!blockValue) return null;

  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: '#F9F9F9',
        paddingY: '1rem',
        paddingX: '1.75rem',
        borderRadius: '1rem',
        display: 'flex',
        gap: '0.75rem',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
      }}
    >
      <BlockNameHeading title='Who We Are' />
      <Box>
        <Typography
          sx={{
            fontFamily: 'Lato',
            fontSize: '1rem',
            fontWeight: '400',
            textAlign: 'justify',
          }}
        >
          {blockValue}
        </Typography>
      </Box>
    </Box>
  );
}

function DemographicsMap({ webmapID, token, addressInfo }) {
  const { getAccessToken } = useAuth();
  const mapRef = useRef(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const viewRef = useRef(null);

  useEffect(() => {
    if (!webmapID || !token || !addressInfo) return;

    let view;
    loadModules([
      'esri/Map',
      'esri/views/MapView',
      'esri/WebMap',
      'esri/geometry/Extent',
      'esri/Graphic',
      'esri/widgets/Legend',
      'esri/config',
      'esri/layers/FeatureLayer',
    ]).then(
      ([Map, MapView, WebMap, Extent, Graphic, Legend, esriConfig, FeatureLayer]) => {
        if (!webmapID || !token) return;

        esriConfig.apiKey = token;

        const extent = new Extent({
          xmin: addressInfo.address_xmin,
          ymin: addressInfo.address_ymin,
          xmax: addressInfo.address_xmax,
          ymax: addressInfo.address_ymax,
          spatialReference: {
            wkid: addressInfo.spatialReference?.wkid || 4326,
          },
        });

        const webmap = new WebMap({
          portalItem: {
            id: webmapID,
          },
        });

        // Redraw the shaded layer from 2026 GeoEnrichment values (flagged; any
        // failure or signed-out view keeps the authored 2024 layer).
        webmap.when(() => {
          removeRetiredLayers(webmap);
          applyDiscernShading({
            webmap,
            addressInfo,
            getAccessToken,
            deps: { FeatureLayer, Graphic },
            isStale: () => view?.destroyed === true || webmap?.destroyed === true,
            onUnavailable: () => addUnavailableNotice(view),
          });
        });

        view = new MapView({
          container: mapRef.current,
          map: webmap,
          extent: extent,
          zoom: 14,
          navigation: {
            mouseWheelZoomEnabled: false,
          },
        });

        viewRef.current = view;

        view
          .when(() => {
            setIsMapLoaded(true);
            setErrorMessage(null);

            view.ui.add(
              new Legend({
                view: view,
              }),
              'top-right',
            );

            if (
              addressInfo.address_longitude &&
              addressInfo.address_latitude
            ) {
              const customSymbol = {
                type: 'picture-marker',
                url: marker,
                width: '50px',
                height: '100px',
              };

              view.graphics.add(
                new Graphic({
                  geometry: {
                    type: 'point',
                    longitude: addressInfo.address_longitude,
                    latitude: addressInfo.address_latitude,
                  },
                  symbol: customSymbol,
                }),
              );
            }
          })
          .catch(err => {
            console.error('Error loading demographics map:', err);
            setIsMapLoaded(false);
            setErrorMessage(err.message || 'Failed to load map');
          });
      },
    );

    return () => {
      if (view) {
        view.destroy();
      }
    };
  }, [webmapID, token, addressInfo]);

  return (
    <Box
      sx={{
        width: '100%',
        height: {
          sm: '30rem',
          xs: '20rem',
        },
        position: 'relative',
      }}
    >
      {!isMapLoaded && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          }}
        >
          {errorMessage ? (
            <Typography
              variant='body2'
              sx={{ color: '#b00020', textAlign: 'center', p: 2 }}
            >
              {errorMessage}
            </Typography>
          ) : (
            <CircularProgress sx={{ color: '#4FA490' }} />
          )}
        </Box>
      )}
      <Box
        ref={mapRef}
        sx={{
          width: '100%',
          height: '100%',
          visibility: isMapLoaded ? 'visible' : 'hidden',
        }}
      />
    </Box>
  );
}

const DemographicsCard = ({ type, addressInfo, demographicsData, webmapID, arcgisToken }) => {
  if (type === 'map') {
    if (!webmapID || !arcgisToken || !addressInfo) {
      return (
        <Box
          sx={{
            height: '256px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F3F4F6',
            borderRadius: '0.5rem',
            border: '2px dashed #D1D5DB',
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
            Search for an address to view demographics map
          </Typography>
        </Box>
      );
    }

    return <DemographicsMap webmapID={webmapID} token={arcgisToken} addressInfo={addressInfo} />;
  }

  if (type === 'data') {
    if (!demographicsData || Object.keys(demographicsData).length === 0) {
      return (
        <Box
          sx={{
            height: '256px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F3F4F6',
            borderRadius: '0.5rem',
            border: '2px dashed #D1D5DB',
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
            Search for an address to view demographics data
          </Typography>
        </Box>
      );
    }

    const blocks = [];

    if (demographicsData.grade) {
      blocks.push(
        <GenericBlock
          key='grade'
          sectionBlock={{
            title: 'Grade',
            value: { grade: demographicsData.grade },
            outfields: ['grade'],
            key: 'demographics',
          }}
          tooltipTitle='Grade'
          hideTooltips
        />,
      );
    }

    if (demographicsData.group) {
      blocks.push(
        <DemographicGroupBlock key='group' group={demographicsData.group} />,
      );
    }

    if (demographicsData.nearest_groups && Array.isArray(demographicsData.nearest_groups)) {
      blocks.push(
        <NearestGroupsBlock
          key='nearest_groups'
          groups={demographicsData.nearest_groups}
        />,
      );
    }

    if (demographicsData.who_we_are) {
      blocks.push(
        <WhoWeAreBlock
          key='who_we_are'
          blockValue={demographicsData.who_we_are}
        />,
      );
    }

    if (blocks.length === 0) {
      return (
        <Box
          sx={{
            height: '256px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F3F4F6',
            borderRadius: '0.5rem',
            border: '2px dashed #D1D5DB',
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
            No demographics data available
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        {blocks.map(block => block)}
      </Box>
    );
  }

  return null;
};

export default DemographicsCard;

